import { useState } from 'react';
import { comments, pollOptions } from '@/mocks/homeData';
import SectionHeading from './SectionHeading';

export default function CommunitySection() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [voted, setVoted] = useState<string>('poll-1');
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const [expanded, setExpanded] = useState(false);

  const totalVotes = pollOptions.reduce((sum, o) => sum + o.votes, 0) + 1;
  const visibleComments = expanded ? comments : comments.slice(0, 4);

  return (
    <section id="community" className="w-full bg-background-50 border-b border-background-200">
      <div className="px-4 md:px-6 lg:px-10 py-12 md:py-16">
        <SectionHeading
          index="05"
          kicker="DISCUSSION"
          title="互动区：评论区的补充线索，往往比正文更细"
          desc="每组长图发布后，最值得看的补充常常出现在评论里。把讨论氛围放在显眼位置，方便逐条对照。"
        />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-300 bg-background-50 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200 bg-background-100">
                <h3 className="font-heading text-base font-bold text-foreground-950">热议 · 1,486 条</h3>
                <div className="flex items-center gap-1.5 text-[11px] font-label">
                  {['最新', '最热'].map((tab, i) => (
                    <span key={tab} className={`h-6 px-2.5 inline-flex items-center rounded-full ${i === 1 ? 'bg-foreground-950 text-background-50' : 'text-foreground-600'}`}>
                      {tab}
                    </span>
                  ))}
                </div>
              </div>

              <ul className="divide-y divide-background-200">
                {visibleComments.map((c) => {
                  const isLiked = !!liked[c.id];
                  return (
                    <li key={c.id} className="px-5 py-4">
                      <div className="flex gap-3">
                        <span className="w-9 h-9 shrink-0 rounded-full bg-secondary-200 text-secondary-900 flex items-center justify-center font-heading font-bold">
                          {c.initial}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground-900 font-label">{c.user}</span>
                            {c.badge && (
                              <span className="h-5 px-2 inline-flex items-center rounded bg-primary-100 text-primary-700 text-[11px] font-label">
                                {c.badge}
                              </span>
                            )}
                            <span className="text-[11px] text-foreground-500 font-label">{c.time}</span>
                          </div>
                          <p className="mt-2 text-sm leading-relaxed text-foreground-700 font-body">{c.content}</p>
                          <div className="mt-2.5 flex items-center gap-4 text-[11px] text-foreground-500 font-label">
                            <button
                              type="button"
                              onClick={() => setLiked((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                              className={`flex items-center gap-1 transition-colors cursor-pointer ${isLiked ? 'text-primary-600' : 'hover:text-foreground-800'}`}
                            >
                              <i className={isLiked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                              {c.likes + (isLiked ? 1 : 0)}
                            </button>
                            <button type="button" className="flex items-center gap-1 hover:text-foreground-800 transition-colors cursor-pointer">
                              <i className="ri-chat-3-line"></i>
                              回复
                            </button>
                            <button type="button" className="flex items-center gap-1 hover:text-foreground-800 transition-colors cursor-pointer">
                              <i className="ri-share-forward-line"></i>
                              转发
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {!expanded && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="w-full py-3.5 text-sm font-label text-foreground-700 hover:text-primary-600 border-t border-background-200 transition-colors cursor-pointer"
                >
                  展开全部评论
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-lg border border-background-300 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">参与讨论</h3>
              <p className="mt-1.5 text-xs text-foreground-600 font-label">补充你发现的线索，或指出哪一层对不上</p>
              <div className="mt-4">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value.slice(0, 300))}
                  rows={4}
                  placeholder="例如：第 4 张长图的水印位置，和其他图对不上……"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 p-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors font-label"
                ></textarea>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[11px] text-foreground-500 font-label">{draft.length} / 300</span>
                  <span className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-md bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors">
                      <i className="ri-image-line"></i>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (!draft.trim()) {
                          setNotice('先写下你想补充的线索');
                          return;
                        }
                        setNotice('已记录你的线索，展示完成后会出现在讨论区');
                        setDraft('');
                      }}
                      className="h-9 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      发布线索
                    </button>
                  </span>
                </div>
                {notice && (
                  <p className="mt-3 text-xs text-primary-700 bg-primary-100/70 rounded-md px-3 py-2 font-label animate-rise">{notice}</p>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-background-300 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-bold text-foreground-950">你会先看哪一层</h3>
                <span className="text-[11px] text-foreground-500 font-label">{totalVotes} 人已投</span>
              </div>
              <div className="mt-4 space-y-3">
                {pollOptions.map((opt) => {
                  const votes = opt.votes + (voted === opt.id ? 1 : 0);
                  const percent = Math.round((votes / totalVotes) * 100);
                  const isActive = voted === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setVoted(opt.id)}
                      className="w-full text-left cursor-pointer group"
                    >
                      <div className="flex items-center justify-between text-xs font-label">
                        <span className={isActive ? 'text-primary-700 font-medium' : 'text-foreground-700'}>{opt.label}</span>
                        <span className="text-foreground-500">{votes} 票</span>
                      </div>
                      <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                        <span
                          className={`block h-full rounded-full transition-all duration-500 ${isActive ? 'bg-primary-500' : 'bg-secondary-400 group-hover:bg-secondary-500'}`}
                          style={{ width: `${percent}%` }}
                        ></span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}